import { Controller } from "@hotwired/stimulus";
import React from "react";
import {
  trapFocus,
  releaseFocusTrap,
  trapNestedModal,
  releaseNestedModal,
  getNestedModalParent,
} from "../utils/FocusTrap";
import { seemsOffline, redirectToOfflinePage } from "../utils/connectivity";
import { shouldActivateClickEvent } from "../utils/keyboardAccessibility";
import { clearLocalCaseProgress } from "../utils/clear_local_case_progress";

export default class extends Controller {
  previouslyFocusedElement = null;
  static targets = ["synopsisModal", "learningObjectivesModal", "documentTitle",
  "documentSynopsis", "documentLO", "documentTitleLOModal", "openQuestionButton",
  "MODMapModal", "documentTitleMMModal",
  "modMap", "documentIntegration",
  "basketModal", "addCourseModal", "addTodoModal",
  "todoModal", "prereqTodoModal", "documentIdInput",
  "subservientIdInput", "modalSubservientSubtitle", "modalSubtitle",
  "modalPrerequisiteSubtitle", "clearNoteField", "clearDateField",
  "clearSubNoteField", "clearSubDateField", "clearPreNoteField", "clearPreDateField", "quizModal", "documentTitleQuizModal", "questionsAnsweredQuizModal","percentCorrectQuizModal",
  "saveQuizTodoModal", "quizName", "quizId","resetModal", "removeModal"];

  connect() {
    this.handleKeydown = this.handleKeydown.bind(this);
    document.addEventListener('keydown', this.handleKeydown);
  }

  disconnect() {
    document.removeEventListener('keydown', this.handleKeydown);
  }

  handleKeydown(event) {
    if (this.hasSaveQuizTodoModalTarget && !this.saveQuizTodoModalTarget.classList.contains('hidden')) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.suppressNextEscapeKeyup();
        this.hideSaveQuizTodoModal(event);
      }
      return;
    }

    if (this.hasQuizModalTarget && !this.quizModalTarget.classList.contains('hidden')) {
      if (event.key === 'Escape') {
        this.hideQuizModal(event);
      }
    }

    // Allow modals to open on Enter or Space
    if (this.hasSynopsisModalTarget && !this.synopsisModalTarget.classList.contains('hidden')) {
      let cancelButton = this.synopsisModalTarget.querySelector('.cancel-button-class');
      if (event.key === 'Escape') {
        this['hideSynopsisModal'](event);
      }
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      }
    }

    if (this.hasLearningObjectivesModalTarget && !this.learningObjectivesModalTarget.classList.contains('hidden')) {
      let cancelButton = this.learningObjectivesModalTarget.querySelector('.cancel-button-class');
      if (event.key === 'Escape') {
        this['hideLearningObjectivesModal'](event);
      }
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      }
    }

    if (this.hasMODMapModalTarget && !this.MODMapModalTarget.classList.contains('hidden')) {
      let cancelButton = this.MODMapModalTarget.querySelector('.cancel-button-class');
      if (event.key === 'Escape') {
        this['hideModMapModal'](event);
      }
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      }
    }
  }

  handleModalKeydown(event, modal, hideModalMethod) {
    let submitButton = modal.querySelector('input[type="submit"]');
    let cancelButton = modal.querySelector('.cancel-button-class');

    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      // Trigger the submit button click event
      submitButton.click();
    } else if (event.key === 'Escape') {
      // Trigger the cancel button click event
      this[hideModalMethod](event);
    }
  }

  openSynopsisModal(event){
    if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') {
      return;
    }
    if (event.type === 'keydown') {
      event.preventDefault(); // Prevent scrolling on Space
    }
    this.previouslyFocusedElement = event.currentTarget;
    const finalDiagnosis = JSON.parse(event.currentTarget.dataset.contentLibraryModalFinalDiagnosisParam || "[]");
    const clinicalExcellence = JSON.parse(event.currentTarget.dataset.contentLibraryModalClinicalExcellenceParam || "[]");
    const basicScienceDisciplines = JSON.parse(event.currentTarget.dataset.contentLibraryModalBasicScienceDisciplinesParam || "[]");
    const documentKind = event.currentTarget.dataset.contentLibraryModalDocumentKindParam || "";
    const title = event.currentTarget.dataset.contentLibraryModalTitleParam || "";
    const synopsis = event.currentTarget.dataset.contentLibraryModalSynopsisParam || "";

    this.documentTitleTarget.innerHTML = title;
    this.documentSynopsisTarget.innerHTML = synopsis;
    this.documentIntegrationTarget.innerHTML = "";

    if(documentKind !== 'illness_script') {
      let finalDiagnosisHtml = finalDiagnosis.length !== 0 ?
        "<div>Final Diagnosis: " + finalDiagnosis.map((final_diagnosis) => {
          return `<span class=\"final-diagnosis\"> ${final_diagnosis} </span>`;
        }).join("") + "</div>" : "";
      let clinicalExcellenceHtml = clinicalExcellence.length !== 0 ?
        "<div>Clinical Excellence Curricular Thread: " + clinicalExcellence.map((obj) => {
          return `<span class=\"clinical-excellence\">${obj}</span>`;
        }).join("") + "</div>" : "";
      let basicScienceDisciplineHtml = basicScienceDisciplines.length !== 0 ?
        "<div>Basic Science Curricular Thread: " + basicScienceDisciplines.map((obj) => {
          return `<span class=\"basic_science_discipline\">${obj}</span>`;
        }).join("") + "</div>" : "";
      this.documentIntegrationTarget.innerHTML = finalDiagnosisHtml + clinicalExcellenceHtml + basicScienceDisciplineHtml;
    }
    this.synopsisModalTarget.classList.remove("hidden");
    this.trapFocus(this.synopsisModalTarget);

    // Scroll to top of modal content
    this.synopsisModalTarget.scrollTop = 0;
  }

  hideSynopsisModal(){
    this.synopsisModalTarget.classList.add("hidden");
    this.releaseFocus();
    if (this.previouslyFocusedElement) {
      this.previouslyFocusedElement.focus();
    }
  }
  // Focus trap helpers
  trapFocus(modal) {
    if (!modal) return;
    const focusableSelectors = 'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]';
    const focusableEls = modal.querySelectorAll(focusableSelectors);
    if (focusableEls.length === 0) return;

    const firstEl = focusableEls[0];
    const lastEl = focusableEls[focusableEls.length - 1];

    modal.addEventListener('keydown', this.handleTabKey = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        }
      } else {
        if (document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    });

    // Focus the first element
    firstEl.focus();
  }

  releaseFocus() {
    if (this.synopsisModalTarget) {
      this.synopsisModalTarget.removeEventListener('keydown', this.handleTabKey);
    }
    if (this.previouslyFocusedElement) {
      this.previouslyFocusedElement.focus();
    }
  }

  openBasketModal(){
    this.basketModalTarget.classList.remove("hidden");
  }

  hideBasketModal(){
    this.basketModalTarget.classList.add("hidden");
  }

  openLearningObjectivesModal(event){
    // Only open on click, Enter, or Space
    if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') {
      return;
    }
    if (event.type === 'keydown') {
      event.preventDefault();
    }
    this.previouslyFocusedElement = event.currentTarget;
    this.documentTitleLOModalTarget.innerHTML=event.params.title
    const searchText = event.params.searchText
    const renderedLearningObjectives = event.params.lo.map((lo) => {
      if (searchText !== "" && lo.toLowerCase().includes(searchText.toLowerCase())) {
        const regex = new RegExp(searchText, 'gi');
        lo = lo.replace(regex, (match) => `<span class=\"search-hit\">${match}</span>`);
      }
      return `
        <div class=\"doc-learning-objective\">
          <div class=\"learning-objective-arrow\"></div>
          <div class=\"learning-objective-content\">
            ${lo}
          </div>
        </div>
      `;
    }).join("");

    this.documentLOTarget.innerHTML=renderedLearningObjectives
    this.learningObjectivesModalTarget.classList.remove("hidden");
    this.trapFocus(this.learningObjectivesModalTarget);
  }

  hideLearningObjectivesModal(){
    this.learningObjectivesModalTarget.classList.add("hidden");
    this.releaseFocus();
    if (this.previouslyFocusedElement) {
      this.previouslyFocusedElement.focus();
    }
  }

  openModMapModal(event){
    // Only open on click, Enter, or Space
    if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') {
      return;
    }
    if (event.type === 'keydown') {
      event.preventDefault();
    }
    this.previouslyFocusedElement = event.currentTarget;
    this.documentTitleMMModalTarget.innerHTML=event.params.title
    this.modMapTarget.innerHTML=`<img src="${event.params.mm}"></img>`
    this.MODMapModalTarget.classList.remove("hidden");
    this.trapFocus(this.MODMapModalTarget);
  }

  hideModMapModal(){
    this.MODMapModalTarget.classList.add("hidden");
    this.releaseFocus();
    if (this.previouslyFocusedElement) {
      this.previouslyFocusedElement.focus();
    }
  }

  openAddCourseModal() {
    this.addCourseModalTarget.classList.remove("hidden");
  }

  hideAddCourseModal() {
    this.addCourseModalTarget.classList.add("hidden");
  }

  openAddTodoModal() {
    this.addTodoModalTarget.classList.remove("hidden");
  }

  hideAddTodoModal() {
    this.addTodoModalTarget.classList.add("hidden");
  }

  openPrereqTodoModal(e) {
    const documentId = e.target.dataset.documentId;
    const documentTitle = e.target.dataset.documentTitle;
    const prerequisiteTitle = e.target.dataset.prerequisiteTitle;

    this.modalSubservientSubtitleTarget.innerHTML = documentTitle;
    this.modalPrerequisiteSubtitleTarget.innerHTML = prerequisiteTitle;
    this.subservientIdInputTarget.value = documentId;
    this.clearSubNoteFieldTarget.value = "";
    this.clearSubDateFieldTarget.value = "";
    this.clearPreNoteFieldTarget.value = "";
    this.clearPreDateFieldTarget.value = "";
    this.prereqTodoModalTarget.classList.remove("hidden");
  }

  hidePrereqTodoModal() {
    this.prereqTodoModalTarget.classList.add("hidden");
  }

  openTodoModal(e) {
    fetch(e.target.dataset.url)
      .then (response => response.text())
      .then(html => Turbo.renderStreamMessage(html))
      .then(this.todoModalTarget.classList.remove("hidden"));
  }

  hideTodoModal(e) {
    this.todoModalTarget.classList.add("hidden");
  }

  openQuizModal(event){
    if (!shouldActivateClickEvent(event)) return;

    event.preventDefault();
    this.previouslyFocusedElement = event.currentTarget;
    const dataset = event.currentTarget.dataset;

    this.populateQuizModalContent(dataset);

    if (!this.hasQuizModalTarget) return;

    this.quizModalTarget.classList.remove("hidden");
    document.body.classList.add("modal-active");

    const heading = document.getElementById('quiz-modal-heading');
    if (heading) heading.classList.add('current-element');

    trapFocus(this.quizModalTarget, {
      previousFocusElement: this.previouslyFocusedElement,
      initialFocusSelector: '#quiz-modal-heading',
    });
  }

  populateQuizModalContent(dataset) {
    if (this.hasDocumentTitleQuizModalTarget) {
      const documentTitle = dataset.documentTitle || dataset.quizName || "";
      this.documentTitleQuizModalTarget.textContent = documentTitle;
    }
    if (this.hasQuestionsAnsweredQuizModalTarget) {
      this.questionsAnsweredQuizModalTarget.textContent = dataset.questionsAnswered;
    }

    const percentCorrect = dataset.percentageCorrect;
    const legacyPieDiv = document.getElementById("saq-modal-pie-div");
    if (legacyPieDiv) {
      legacyPieDiv.style = `--p:${percentCorrect};--c:#1d7360;--b:15px;`;
    }
    if (this.hasPercentCorrectQuizModalTarget) {
      this.percentCorrectQuizModalTarget.textContent = `${percentCorrect}%`;
    }

    const openQuestionLink = document.getElementById("open-question");
    const quizId = dataset.quizId;
    if (openQuestionLink && quizId) {
      openQuestionLink.href = openQuestionLink.href.replace(/\/id\//, `/${quizId}/`);
    }

    const resetOpenQuestionLink = document.getElementById("reset-open-question");
    if (resetOpenQuestionLink && quizId) {
      resetOpenQuestionLink.href = resetOpenQuestionLink.href.replace(/\/id\//, `/${quizId}/`);
    }

    if (this.hasQuizIdTarget) {
      this.quizIdTarget.value = quizId;
    }
    if (this.hasQuizNameTarget) {
      this.quizNameTarget.textContent = dataset.quizName;
    }
  }

  hideQuizModal(event){
    if (this.hasSaveQuizTodoModalTarget && !this.saveQuizTodoModalTarget.classList.contains('hidden')) {
      releaseNestedModal(this.quizModalTarget, this.saveQuizTodoModalTarget, { restoreFocus: false });
      this.saveQuizTodoTrigger = null;
    }

    const heading = document.getElementById('quiz-modal-heading');
    if (heading) heading.classList.remove('current-element');

    if (!this.hasQuizModalTarget) {
      this.cleanupModalAccessibility();
      return;
    }

    this.quizModalTarget.inert = false;
    releaseFocusTrap(this.quizModalTarget);
    this.quizModalTarget.classList.add("hidden");
    this.quizModalTarget.removeAttribute('aria-hidden');
    this.cleanupModalAccessibility();
    this.previouslyFocusedElement = null;
  }

  cleanupModalAccessibility() {
    document.body.classList.remove("modal-active");
  }

  openSaveQuizTodoModal(event){
    if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') {
      return;
    }
    if (event.type === 'keydown') {
      event.preventDefault();
    }
    event.preventDefault();
    event.stopPropagation();

    if (seemsOffline()) {
      redirectToOfflinePage();
      return;
    }

    this.saveQuizTodoTrigger = event.currentTarget;

    const parentModal = getNestedModalParent();

    trapNestedModal(parentModal, this.saveQuizTodoModalTarget, {
      trigger: this.saveQuizTodoTrigger,
      initialFocusSelector: '.select-list-save-quiz-todo-modal',
    });
  }

  hideSaveQuizTodoModal(event){
    const triggerElement = event?.currentTarget;
    const isSubmitControl = triggerElement &&
      (
        (triggerElement.tagName === "INPUT" && triggerElement.type === "submit") ||
        (triggerElement.tagName === "BUTTON" && triggerElement.type === "submit")
      );

    if (isSubmitControl) {
      return;
    }

    if (event) {
      event.preventDefault();
    }

    if (!this.hasSaveQuizTodoModalTarget) return;

    const parentModal = getNestedModalParent();

    releaseNestedModal(parentModal, this.saveQuizTodoModalTarget);

    if (this.saveQuizTodoModalTarget.dataset.redirectTo) {
      window.location = this.saveQuizTodoModalTarget.dataset.redirectTo;
      return;
    }

    if (!parentModal) {
      this.cleanupModalAccessibility();
    }

    this.saveQuizTodoTrigger = null;
  }

  suppressNextEscapeKeyup() {
    const suppress = (event) => {
      if (event.key === "Escape" || event.keyCode === 27) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
      window.removeEventListener("keyup", suppress, true);
    };

    window.addEventListener("keyup", suppress, true);
  }

  openResetModal(e){
    e.preventDefault();
    e.stopImmediatePropagation();
    const resetModalId = e.target.dataset.resetModalId;
    const modal = this.resetModalTargets.find(target => target.id === `reset_modal_${resetModalId}`);
    if (modal) {
        modal.classList.remove("hidden");
    }
  }

  hideResetModal(e){
    const resetModalId = e.target.dataset.resetModalId;
    const modal = this.resetModalTargets.find(target => target.id === `reset_modal_${resetModalId}`);
    if (modal) {
        modal.classList.add("hidden");
    }
  }

  openRemoveModal(e){
    e.preventDefault();
    e.stopImmediatePropagation();
    const removeModalId = e.target.dataset.removeModalId;
    const modal = this.removeModalTargets.find(target => target.id === `remove_modal_${removeModalId}`);
    if (modal) {
      modal.classList.remove("hidden");
    }
  }

  hideRemoveModal(e){
    const removeModalId = e.target.dataset.removeModalId;
    const modal = this.removeModalTargets.find(target => target.id === `remove_modal_${removeModalId}`);
    if (modal) {
      modal.classList.add("hidden");
    }
  }

  removeStudentChoiceCase(event) {    
    const documentId = event.target.dataset.documentId;
    const documentSetId = event.target.dataset.documentSetId;
    const url = `/document_sets/${documentSetId}/remove_student_choice_case/${documentId}`;

    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content,
      },
      body: JSON.stringify({ document_id: documentId }),
    })
      .then((response) => {
        if (!response.ok) throw new Error("API call failed");
        return response.json();
      })
      .then(() => {
        window.location.reload();
      })
      .catch(() => {
        alert("Something went wrong. Please try again.");
      });
  }

  async resetCase(event) {
    const documentId = event.target.dataset.documentId;
    const documentSetId = event.target.dataset.documentSetId;
    const sourcePage = event.target.dataset.sourcePage;
    const addItemPage = event.target.dataset.addItemPage;
    const url = `/documents/${documentId}/reset_case?document_set_id=${documentSetId}&source_page=${sourcePage}&add_item_page=${addItemPage}`;

    await clearLocalCaseProgress(Number(documentId));

    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content,
      },
      body: JSON.stringify({ document_id: documentId }),
    })
    .then((response) => {
      if (!response.ok) throw new Error("API call failed");
      return response.json();
    })
    .then(() => {
      window.location.reload();
    })
    .catch(() => {
      alert("Something went wrong. Please try again.");
    });
  }
}
